/**
 * The linking twin of `ProviderSignInPanel`. It deliberately does not modify
 * the two shared sign-in buttons, and it hands a `CONFLICT` refusal back to
 * the caller rather than the shared error modal, because design prompt 44.4
 * mandates a titled two-button dialog the modal cannot render.
 */
import {
  useOauthAppleLink,
  useOauthGoogleLink,
} from "@sportsmet/auth-sdk/private";
import type { ReactNode } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { AppleSignInButton } from "../signIn/AppleSignInButton";
import { GoogleSignInButton } from "../signIn/GoogleSignInButton";

export type typeLinkProviderPanelProps = {
  provider: "apple" | "google";
  /** apple.webServicesId when provider is "apple", else null. */
  webServicesId: string | null;
  /** google.webClientId when provider is "google", else null. */
  webClientId: string | null;
  /** WI-6 supplies the specific-copy handler for a CONFLICT refusal. */
  onConflict: () => void;
};

export const LinkProviderPanel = ({
  provider,
  webServicesId,
  webClientId,
  onConflict,
}: typeLinkProviderPanelProps): ReactNode => {
  // Both hooks are called unconditionally, at the top of the component, then
  // one is selected. React's rules of hooks forbid calling either behind a
  // ternary.
  const appleLink = useOauthAppleLink();
  const googleLink = useOauthGoogleLink();
  const link = provider === "apple" ? appleLink : googleLink;

  const handleIdToken = ({ idToken }: { idToken: string }): void => {
    link.mutate(
      { idToken },
      {
        onSuccess: (result) => {
          if (extractErrorCode(result) === "CONFLICT") {
            onConflict();
            return;
          }
          if (showApiError(result)) return;
          // Success. Nothing else to do: the generated hook already
          // invalidates identity/methods-get, so the row flips to Connected
          // on its own.
        },
      },
    );
  };

  if (provider === "apple" && webServicesId !== null) {
    return (
      <AppleSignInButton
        webServicesId={webServicesId}
        onIdToken={handleIdToken}
        onFailed={() => {
          // The one client-raised code in this flow. `showApiError` reads
          // only `error.code` and maps it through the shared catalog, so the
          // message is deliberately empty.
          showApiError({
            error: { code: "PROVIDER_UNAVAILABLE", message: "" },
            data: null,
          });
        }}
      />
    );
  }

  if (provider === "google" && webClientId !== null) {
    return (
      <GoogleSignInButton webClientId={webClientId} onIdToken={handleIdToken} />
    );
  }

  return null;
};
